import { createSlice } from "@reduxjs/toolkit";
import { thunkError } from "@/widgets/common/createDataWidget";
import type { SortRule } from "@/shared/types";
import { DEFAULT_SORT } from "./config";
import { loadBasicInformation, loadMoreBasicInformation } from "./thunks";
import type { BasicInformationTableState } from "./types";

const initialState: BasicInformationTableState = {
  data: [],
  status: "idle",
  error: null,
  // The widget's first fetch runs with this sort, so the loaded page order
  // matches the table's default email-ascending header state.
  sort: DEFAULT_SORT,
  page: 0,
  total: 0,
  loadingMore: false,
};

export const basicInformationTableSlice = createSlice({
  name: "basicInformationTable",
  initialState,
  reducers: {
    setSort(state, action: { payload: SortRule[] }) {
      state.sort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBasicInformation.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadBasicInformation.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload.rows;
        state.page = action.payload.pageIndex;
        state.total = action.payload.total;
      })
      .addCase(loadBasicInformation.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.status = "error";
        state.error = thunkError(action.error.message);
      })
      // Load-more lifecycle: the bottom banner (loadingMore) shows while the
      // next page is in flight; rows append deduped by stable id (section 53).
      .addCase(loadMoreBasicInformation.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(loadMoreBasicInformation.fulfilled, (state, action) => {
        const seen = new Set(state.data.map((row) => row.id));
        state.data.push(
          ...action.payload.rows.filter((row) => !seen.has(row.id)),
        );
        state.page = action.payload.pageIndex;
        state.total = action.payload.total;
        state.loadingMore = false;
      })
      .addCase(loadMoreBasicInformation.rejected, (state) => {
        state.loadingMore = false;
      });
  },
});

export const { setSort: setBasicInformationSort } = basicInformationTableSlice.actions;
export const basicInformationTableReducer = basicInformationTableSlice.reducer;
